// 用户仪表板侧边栏组件
// 左侧固定导航，展示用户相关菜单

import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Bell,
  Blocks,
  Calendar,
  ChevronRight,
  Gamepad2,
  LayoutDashboard,
  Link2,
  Menu,
  Palette,
  Settings,
  ShoppingCart,
  Sparkles,
} from 'lucide-react'

import { useUserSidebarSummary } from '@/hooks/useSidebarSummaryQuery'
import { cn } from '@/lib/utils'
import { resolveMediaUrl } from '@/lib/media'
import { useAuthStore } from '@/stores/authStore'
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetHandle,
  BottomSheetHeader,
  BottomSheetTitle,
} from '@/components/ui/mobile-bottom-sheet'

interface MenuItem {
  path: string
  label: string
  icon: React.ReactNode
}

interface UserSidebarProps {
  className?: string
}

function isUserMenuItemActive(path: string, pathname: string) {
  return path === '/user' ? pathname === '/user' : pathname.startsWith(path)
}

function getActiveUserMenuItem(pathname: string) {
  return (
    userMenuItems.find(item => isUserMenuItemActive(item.path, pathname)) ??
    userMenuItems[0]
  )
}

export const userMenuItems: MenuItem[] = [
  {
    path: '/user',
    label: '仪表板',
    icon: <LayoutDashboard className="h-5 w-5" />,
  },
  {
    path: '/user/checkin',
    label: '每日签到',
    icon: <Calendar className="h-5 w-5" />,
  },
  {
    path: '/user/game-accounts',
    label: '游戏账号',
    icon: <Gamepad2 className="h-5 w-5" />,
  },
  {
    path: '/user/skins',
    label: '我的皮肤',
    icon: <Palette className="h-5 w-5" />,
  },
  {
    path: '/user/blueprints',
    label: '我的蓝图',
    icon: <Blocks className="h-5 w-5" />,
  },
  {
    path: '/user/activities',
    label: '活动中心',
    icon: <Sparkles className="h-5 w-5" />,
  },
  {
    path: '/user/official-accounts',
    label: '正版验证',
    icon: <Link2 className="h-5 w-5" />,
  },
  {
    path: '/user/recharge-records',
    label: '充值记录',
    icon: <ShoppingCart className="h-5 w-5" />,
  },
  {
    path: '/user/notifications',
    label: '通知中心',
    icon: <Bell className="h-5 w-5" />,
  },
  {
    path: '/user/settings',
    label: '账号设置',
    icon: <Settings className="h-5 w-5" />,
  },
]

function getUserMenuBadge(path: string, summary?: ReturnType<typeof useUserSidebarSummary>['data']) {
  const unreadCount = summary?.unreadNotificationCount || 0
  const pendingCheckInCount = summary?.pendingCheckInCount || 0
  const activityClaimableCount = summary?.activityClaimableCount || 0
  const pendingGameAccountLoginRequestCount =
    summary?.pendingGameAccountLoginRequestCount || 0

  if (path === '/user/notifications' && unreadCount > 0) {
    return unreadCount
  }
  if (path === '/user/checkin' && pendingCheckInCount > 0) {
    return pendingCheckInCount
  }
  if (path === '/user/game-accounts' && pendingGameAccountLoginRequestCount > 0) {
    return pendingGameAccountLoginRequestCount
  }
  if (path === '/user/activities' && activityClaimableCount > 0) {
    return activityClaimableCount
  }
  return 0
}

export function UserSidebar({ className }: UserSidebarProps) {
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const { data: sidebarSummary } = useUserSidebarSummary()

  useEffect(() => {
    if (
      location.pathname.startsWith('/user/notifications') ||
      location.pathname.startsWith('/user/skins')
    ) {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
      void queryClient.invalidateQueries({ queryKey: ['skins', 'user'] })
    }
  }, [location.pathname, queryClient])

  return (
    <aside className={cn('fixed left-4 top-20 z-30 h-[calc(100vh-6rem)] w-72', className)}>
      <div className="flex h-full flex-col">
        <div className="app-surface flex h-full flex-col overflow-hidden px-4 py-5 shadow-[0_10px_28px_rgba(15,23,42,0.05)] dark:shadow-[0_20px_48px_rgba(2,6,23,0.4)]">
          <div className="rounded-[1.6rem] bg-gradient-to-br from-indigo-500 via-sky-500 to-cyan-400 p-5 text-white shadow-[0_20px_40px_rgba(79,70,229,0.28)]">
            <div className="flex items-center gap-4">
              <img
                src={resolveMediaUrl(user?.avatar)}
                alt={user?.username || 'Steve'}
                className="h-16 w-16 rounded-[1.35rem] border border-white/35 bg-white/15 object-contain p-1.5 shadow-inner dark:border-white/10 dark:bg-slate-950/20"
                style={{ imageRendering: 'pixelated' }}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-lg font-semibold">{user?.username || '用户中心'}</p>
                <p className="truncate text-sm text-white/75">
                  {user?.qqNumber ? `QQ ${user.qqNumber}` : '欢迎回来'}
                </p>
              </div>
            </div>
            <div className="mt-4 rounded-[1.2rem] border border-white/20 bg-white/10 px-3 py-2 text-sm text-white/85 dark:border-white/10 dark:bg-slate-950/20">
              用户中心
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto px-2 py-5">
            <ul className="space-y-1.5">
              {userMenuItems.map(item => {
                const isActive = isUserMenuItemActive(item.path, location.pathname)
                const badge = getUserMenuBadge(item.path, sidebarSummary)

                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={cn(
                        'flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium transition-all',
                        isActive
                          ? 'bg-gradient-to-r from-indigo-500 to-sky-500 text-white shadow-[0_14px_26px_rgba(79,70,229,0.24)]'
                          : 'text-slate-600 hover:bg-[#eef1f8]/85 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-[#222427]/85 dark:hover:text-slate-100'
                      )}
                    >
                      {item.icon}
                      <span className="flex-1">{item.label}</span>
                      {badge > 0 ? (
                        <span
                          className={cn(
                            'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                            isActive
                              ? 'bg-white/20 text-white dark:bg-slate-950/25'
                              : 'bg-indigo-600 text-white dark:bg-indigo-500'
                          )}
                        >
                          {badge > 99 ? '99+' : badge}
                        </span>
                      ) : null}
                    </NavLink>
                  </li>
                )
              })}
            </ul>
          </nav>

          <div className="app-card-soft mt-auto px-4 py-3">
            <p className="text-xs font-medium text-slate-700 dark:text-slate-200">最新动态</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              活动奖励、公告更新和系统通知
            </p>
          </div>
        </div>
      </div>
    </aside>
  )
}

export function UserMobileNav({ className }: UserSidebarProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  const { data: sidebarSummary } = useUserSidebarSummary()
  const { user } = useAuthStore()
  const activeItem = getActiveUserMenuItem(location.pathname)
  const activeBadge = getUserMenuBadge(activeItem.path, sidebarSummary)

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname, location.search])

  return (
    <div className={cn('space-y-3 2xl:hidden', className)}>
      <div className="sticky top-[4.5rem] z-20">
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="app-card-soft grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-left"
        >
          <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[1rem] border border-border/80 bg-secondary/85 text-foreground">
              {activeItem.icon}
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <p className="text-xs font-medium text-muted-foreground">当前页面</p>
              <p className="truncate text-sm font-semibold text-foreground">{activeItem.label}</p>
            </div>
          </div>
          <div className="flex min-w-0 shrink-0 items-center gap-1.5 sm:gap-2">
            {activeBadge > 0 ? (
              <span className="shrink-0 rounded-full bg-foreground px-2 py-0.5 text-[11px] font-semibold text-background dark:bg-slate-100 dark:text-slate-950">
                {activeBadge > 99 ? '99+' : activeBadge}
              </span>
            ) : null}
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border/80 bg-card/88 px-2 py-1.5 text-sm font-medium text-foreground sm:px-3">
              <Menu className="h-4 w-4" />
              <span className="max-[380px]:hidden">目录</span>
            </span>
          </div>
        </button>
      </div>

      <BottomSheet open={menuOpen} onOpenChange={setMenuOpen}>
        <BottomSheetContent>
          <BottomSheetHeader>
            <BottomSheetHandle />
            <div className="flex items-center gap-3 pr-12">
              <img
                src={resolveMediaUrl(user?.avatar)}
                alt={user?.username || '用户中心'}
                className="h-11 w-11 rounded-[1rem] border border-border/80 bg-secondary/75 object-contain p-1"
                style={{ imageRendering: 'pixelated' }}
              />
              <div className="min-w-0">
                <BottomSheetTitle className="truncate text-left text-base font-semibold text-foreground">
                  {user?.username || '用户中心'}
                </BottomSheetTitle>
                <p className="mt-1 text-sm text-muted-foreground">选择要进入的页面</p>
              </div>
            </div>
          </BottomSheetHeader>

          <div className="max-h-[calc(min(85vh,42rem)-5.5rem)] overflow-y-auto px-4 pb-5 pt-4">
            <div className="space-y-2">
              {userMenuItems.map(item => {
                const isActive = isUserMenuItemActive(item.path, location.pathname)
                const badge = getUserMenuBadge(item.path, sidebarSummary)

                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-3 rounded-[1.2rem] border px-4 py-3 text-sm font-medium transition',
                      isActive
                        ? 'border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-200'
                        : 'border-border/80 bg-card/82 text-foreground hover:bg-accent'
                    )}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[0.95rem] border border-border/70 bg-secondary/78">
                      {item.icon}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {badge > 0 ? (
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                          isActive
                            ? 'bg-indigo-600 text-white dark:bg-indigo-400 dark:text-slate-950'
                            : 'bg-foreground text-background dark:bg-slate-100 dark:text-slate-950'
                        )}
                      >
                        {badge > 99 ? '99+' : badge}
                      </span>
                    ) : null}
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </NavLink>
                )
              })}
            </div>
          </div>
        </BottomSheetContent>
      </BottomSheet>
    </div>
  )
}
